/** @flow */
import React from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import { translate } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { List, ListItem } from 'material-ui/List';
import Divider from 'material-ui/Divider';
import Subheader from 'material-ui/Subheader';
import FeaturePermission from 'common/components/FeaturePermission';
import BetaTitle from 'statsradio/components/BetaTitle';
import * as Routes from 'statsradio/helpers/constant/Routes';

import RadioConfiguredComponent from '../components/RadioConfiguredComponent';
import CurrentRadioHeader from '../modules/radio/CurrentRadioHeader';
import Permissions from '../modules/authorisation/Permissions';
import AuthorisedComponent from '../modules/authorisation/AuthorisedComponent';

import { Colors, Metrics } from '../themes';
import DevFlag from '../../common/components/DevFlag';
import SmallMarketRadioAuthorisation from '../modules/authorisation/SmallMarketAuthorisation';

const Link = styled(NavLink)`
  text-decoration: none;
  color: ${Colors.primaryText};
`;

const activeStyle = {
  color: Colors.primary,
};

type Props = {
  onLinkClick: () => void,
  t: any,
};

function Menu(props: Props) {
  const { t, onLinkClick } = props;

  return (
    <div>
      <CurrentRadioHeader />
      <List>
        <RadioConfiguredComponent>
          <AuthorisedComponent permissions={[
            Permissions.ACCESS_DASHBOARD,
            Permissions.ACCESS_REACH,
            Permissions.ACCESS_LISTENING_TIME,
            Permissions.ACCESS_DEMOGRAPHY,
            Permissions.ACCESS_GEOLOCALISATION]}
          >

            <Subheader>{t('menu.programmingSection')}</Subheader>
            <AuthorisedComponent permissions={Permissions.ACCESS_DASHBOARD}>
              <Link to={Routes.DASHBOARD} activeStyle={activeStyle} onClick={onLinkClick}>
                <ListItem primaryText={t('menu.dashboard')} style={{ color: 'inherit' }}
                  leftIcon={<i className="material-icons">dashboard</i>}
                />
              </Link>
            </AuthorisedComponent>
            <AuthorisedComponent permissions={Permissions.ACCESS_HISTORY}>
              <Link to="/history" activeStyle={activeStyle} onClick={onLinkClick}>
                <ListItem primaryText={<BetaTitle text={t('menu.history')} />} style={{ color: 'inherit' }}
                  leftIcon={<i className="material-icons">history</i>}
                />
              </Link>
            </AuthorisedComponent>
            <SmallMarketRadioAuthorisation>
              <AuthorisedComponent permissions={Permissions.ACCESS_REACH}>
                <Link to={Routes.REACH} activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={t('menu.reach')} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">trending_up</i>}
                  />
                </Link>
              </AuthorisedComponent>
            </SmallMarketRadioAuthorisation>
            <SmallMarketRadioAuthorisation>
              <AuthorisedComponent permissions={Permissions.ACCESS_LISTENING_TIME}>
                <Link to="/listening-time" activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem
                    primaryText={t('menu.listeningTime')}
                    style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">av_timer</i>}
                  />
                </Link>
              </AuthorisedComponent>
            </SmallMarketRadioAuthorisation>
            <SmallMarketRadioAuthorisation>
              <FeaturePermission feature={'DEMOGRAPHY_MODULE'}>
                <AuthorisedComponent permissions={Permissions.ACCESS_DEMOGRAPHY}>
                  <Link to="/demography" activeStyle={activeStyle} onClick={onLinkClick}>
                    <ListItem primaryText={<BetaTitle text={t('menu.demography')} />} style={{ color: 'inherit' }}
                      leftIcon={<i className="material-icons">pie_chart</i>}
                    />
                  </Link>
                </AuthorisedComponent>
              </FeaturePermission>
            </SmallMarketRadioAuthorisation>

            <Divider style={{ marginTop: Metrics.smallMargin }} />
          </AuthorisedComponent>

          <SmallMarketRadioAuthorisation>
            <FeaturePermission feature={'QUALITATIVE_MODULE'}>
              <AuthorisedComponent permissions={Permissions.ACCESS_QUALITATIVE}>
                <Subheader>{t('menu.survey')}</Subheader>
                <Link to="/survey" activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={<BetaTitle text={t('menu.survey')} />} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">pie_chart</i>}
                  />
                </Link>
                <Divider style={{ marginTop: Metrics.smallMargin }} />
              </AuthorisedComponent>
            </FeaturePermission>
          </SmallMarketRadioAuthorisation>

          <DevFlag flag="STREAMING_METRICS">
            <AuthorisedComponent permissions={Permissions.ACCESS_STREAMING}>
              <Subheader>{t('menu.technical')}</Subheader>
              <Link to={Routes.STREAMING} activeStyle={activeStyle} onClick={onLinkClick}>
                <ListItem primaryText={<BetaTitle text={t('menu.streams')} />} style={{ color: 'inherit' }}
                  leftIcon={<i className="material-icons">pie_chart</i>}
                />
              </Link>
              <Divider style={{ marginTop: Metrics.smallMargin }} />
            </AuthorisedComponent>
          </DevFlag>

          <AuthorisedComponent permissions={[
            Permissions.ACCESS_SALES,
            Permissions.ACCESS_SALES_FLYER,
            Permissions.ACCESS_SPOTS,
            Permissions.ACCESS_CAMPAIGNS,
            Permissions.ACCESS_REACH,
            Permissions.MANAGE_CUSTOMERS,
            Permissions.MANAGE_CAMPAIGNS_ADVERTISEMENT]}
          >
            <Subheader>{t('menu.salesSection')}</Subheader>

            <AuthorisedComponent permissions={Permissions.ACCESS_SALES}>
              <FeaturePermission feature="FOLLO_MODULE">
                <Link to="/sales-dashboard" activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={t('menu.dashboard')} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">dashboard</i>}
                  />
                </Link>
              </FeaturePermission>
            </AuthorisedComponent>

            <AuthorisedComponent permissions={Permissions.ACCESS_SALES_FLYER}>
              <Link to="/sales-flyer" activeStyle={activeStyle} onClick={onLinkClick}>
                <ListItem primaryText={t('menu.summary')} style={{ color: 'inherit' }}
                  leftIcon={<i className="material-icons">insert_drive_file</i>}
                />
              </Link>
            </AuthorisedComponent>

            <AuthorisedComponent permissions={Permissions.ACCESS_SALES}>
              <Link to="/estimations" activeStyle={activeStyle} onClick={onLinkClick}>
                <ListItem primaryText={t('menu.estimations')} style={{ color: 'inherit' }}
                  leftIcon={<i className="material-icons">monetization_on</i>}
                />
              </Link>
            </AuthorisedComponent>

            <FeaturePermission feature="FOLLO_MODULE">
              <AuthorisedComponent permissions={[Permissions.ACCESS_SALES]}>
                <Link to="/propositions" activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={<BetaTitle text={t('menu.propositions')} />} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">view_list</i>}
                  />
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={[Permissions.ACCESS_CAMPAIGNS]}>
                <Link to={Routes.CAMPAIGNS} activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={<BetaTitle text={t('menu.campaigns')} />} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">assignment</i>}
                  />
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={[Permissions.ACCESS_ALL_CAMPAIGNS]}>
                <Link to={Routes.ALLCAMPAIGNS} activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={<BetaTitle text={t('menu.allCampaigns')} />} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">assignment</i>}
                  />
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={Permissions.ACCESS_SPOTS}>
                <Link to="/spots" activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={<BetaTitle text={t('menu.spots')} />} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">access_time</i>}
                  />
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={Permissions.MANAGE_CUSTOMERS}>
                <Link to="/customers" activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={t('menu.customers')} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">account_circle</i>}
                  />
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={Permissions.MANAGE_ALL_CUSTOMERS}>
                <Link to="/all-customers" activeStyle={activeStyle} onClick={onLinkClick}>
                  <ListItem primaryText={t('menu.allCustomers')} style={{ color: 'inherit' }}
                    leftIcon={<i className="material-icons">account_circle</i>}
                  />
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={Permissions.ACCESS_SALES}>
                <FeaturePermission feature="SALES_INVENTORY_MODULE">
                  <Link to="/spot-inventory" activeStyle={activeStyle} onClick={onLinkClick}>
                    <ListItem primaryText={t('menu.inventory')} style={{ color: 'inherit' }}
                      leftIcon={<i className="material-icons">view_module</i>}
                    />
                  </Link>

                  <DevFlag flag="SALES_PROMOTIONS">
                    <Link to="/promotions" activeStyle={activeStyle} onClick={onLinkClick}>
                      <ListItem primaryText={t('menu.promotions')} style={{ color: 'inherit' }}
                        leftIcon={<i className="material-icons">whatshot</i>}
                      />
                    </Link>
                  </DevFlag>

                </FeaturePermission>
              </AuthorisedComponent>

            </FeaturePermission>
            <Divider style={{ marginTop: Metrics.smallMargin }} />
          </AuthorisedComponent>
        </RadioConfiguredComponent>

        <AuthorisedComponent permissions={Permissions.MANAGE_USERS}>
          <Subheader>{t('menu.administration')}</Subheader>
          <Link to="/user-management" activeStyle={activeStyle} onClick={onLinkClick}>
            <ListItem primaryText={t('menu.userManagement')} style={{ color: 'inherit' }}
              leftIcon={<i className="material-icons">person</i>}
            />
          </Link>
          <Divider style={{ marginTop: Metrics.margin }} />
        </AuthorisedComponent>

        <AuthorisedComponent permissions={[Permissions.BILL_RADIOS, Permissions.CREATE_DEMO_CAMPAIGN]}>
          <Subheader>{t('menu.statsRadioAdministration')}</Subheader>

          <FeaturePermission feature="FOLLO_MODULE">
            <AuthorisedComponent permissions={Permissions.CREATE_DEMO_CAMPAIGN}>
              <Link to="/create-demo-campaign" activeStyle={activeStyle} onClick={onLinkClick}>
                <ListItem primaryText={t('menu.demoCampaign')} style={{ color: 'inherit' }}
                  leftIcon={<i className="material-icons">assignment</i>}
                />
              </Link>
            </AuthorisedComponent>
          </FeaturePermission>

          <AuthorisedComponent permissions={Permissions.BILL_RADIOS}>
            <Link to={Routes.INVOICING} activeStyle={activeStyle} onClick={onLinkClick}>
              <ListItem primaryText={t('menu.invoicing')} style={{ color: 'inherit' }}
                leftIcon={<i className="material-icons">receipt</i>}
              />
            </Link>
          </AuthorisedComponent>

          <Divider style={{ marginTop: Metrics.margin }} />
        </AuthorisedComponent>

        <DevFlag flag="RADIO_CONFIGURATION">
          <RadioConfiguredComponent allowOnlyNotConfigured>
            <AuthorisedComponent permissions={Permissions.ACCESS_CONFIGURATION}>
              <Subheader>{t('menu.configuration')}</Subheader>
              <Link to="/configure-radio" activeStyle={activeStyle} onClick={onLinkClick}>
                <ListItem primaryText={t('menu.radioConfiguration')} style={{ color: 'inherit' }}
                  leftIcon={<i className="material-icons">settings</i>}
                />
              </Link>
              <Divider style={{ marginTop: Metrics.smallMargin }} />
            </AuthorisedComponent>
          </RadioConfiguredComponent>
        </DevFlag>
        <Link to="/tutorials" activeStyle={activeStyle} onClick={onLinkClick}>
          <ListItem primaryText={t('menu.tutorials')} style={{ color: 'inherit' }}
            leftIcon={<i className="material-icons">school</i>}
          />
        </Link>
      </List>
      <ReactTooltip />
    </div>
  );
}

export default withRouter(translate()(Menu));



// WEBPACK FOOTER //
// ./src/statsradio/layout/Menu.js